import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { proposalsApi, templatesApi, paymentsApi, api, formatApiError } from "./api";
import BlockEditor from "./BlockEditor";
import ImageSelector from "./ImageSelector";
import BookingSummary from "./BookingSummary";
import { ArrowLeft, ExternalLink, Copy, RefreshCw, LogOut, Check, BookmarkPlus, Wallet, Users, Printer } from "lucide-react";

const STATUS_OPTIONS = [
  ["draft", "Borrador"],
  ["sent", "Enviada"],
  ["viewed", "Vista"],
  ["accepted", "Aceptada"],
  ["rejected", "Rechazada"],
  ["booked", "Reservada"],
];

const PAYMENT_STATUS_OPTIONS = [
  ["unpaid", "Sin pagar"],
  ["deposit_pending", "Depósito pendiente"],
  ["deposit_received", "Depósito recibido"],
  ["partial", "Pago parcial"],
  ["paid", "Pagado completamente"],
  ["refunded", "Reembolsado"],
  ["cancelled", "Cancelado"],
];

const BOOK_MODE_OPTIONS = [
  ["", "(usar predeterminado de Pagos)"],
  ["disabled", "Desactivado — sin botón"],
  ["contact_only", "Botón de contacto (email / WhatsApp)"],
  ["accept_form", "Formulario de aceptación del presupuesto"],
  ["custom_url", "Redirigir a URL personalizada"],
  ["deposit", "[Próximamente] Cobrar depósito (Stripe)"],
  ["total", "[Próximamente] Cobrar total (Stripe)"],
  ["stripe_checkout", "[Próximamente] Stripe Checkout"],
];

const FEE_KIND_OPTIONS = [
  ["none", "Sin cargo"],
  ["pct", "% sobre subtotal"],
  ["fixed", "Importe fijo (€)"],
];

export default function AdminProposalEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [proposal, setProposal] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [acceptances, setAcceptances] = useState([]);
  const [requests, setRequests] = useState([]);
  const debounceRef = useRef(null);
  const isDirtyRef = useRef(false);

  // Load
  useEffect(() => {
    let cancelled = false;
    proposalsApi.get(id)
      .then((data) => { if (!cancelled) setProposal(data); })
      .catch((err) => setError(formatApiError(err)));
    paymentsApi.getSettings()
      .then((s) => { if (!cancelled) setPaymentSettings(s); })
      .catch(() => {});
    paymentsApi.acceptances(id)
      .then((a) => { if (!cancelled) setAcceptances(a); })
      .catch(() => {});
    api.get(`/admin/proposals/${id}/requests`)
      .then((r) => { if (!cancelled) setRequests(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [id]);

  // Debounced autosave whenever proposal changes
  const persist = useCallback(async (p) => {
    setSaving(true);
    try {
      const payload = {
        client_name: p.client_name,
        destination: p.destination,
        trip_title: p.trip_title,
        cover_image: p.cover_image,
        date_from: p.date_from,
        date_to: p.date_to,
        status: p.status,
        proposal_type: p.proposal_type,
        share_password: p.share_password,
        expires_at: p.expires_at,
        blocks: p.blocks,
        // Booking / payment
        payment_status: p.payment_status,
        book_mode: p.book_mode,
        book_url: p.book_url,
        book_contact_email: p.book_contact_email,
        book_contact_whatsapp: p.book_contact_whatsapp,
        iva_pct_override: p.iva_pct_override,
        prices_include_iva_override: p.prices_include_iva_override,
        deposit_kind_override: p.deposit_kind_override,
        deposit_value_override: p.deposit_value_override,
        mgmt_fee_kind_override: p.mgmt_fee_kind_override,
        mgmt_fee_value_override: p.mgmt_fee_value_override,
        booking_notes: p.booking_notes,
      };
      const updated = await proposalsApi.update(p.id, payload);
      setSavedAt(new Date());
      isDirtyRef.current = false;
      // sync server timestamps without disturbing edit state
      setProposal((prev) => prev ? { ...prev, updated_at: updated.updated_at } : prev);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }, []);

  const onChange = useCallback((patch) => {
    setProposal((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      isDirtyRef.current = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => persist(next), 800);
      return next;
    });
  }, [persist]);

  const onBlockChange = useCallback((blockId, newBlock) => {
    setProposal((prev) => {
      if (!prev) return prev;
      const blocks = prev.blocks.map((b) => (b.id === blockId ? newBlock : b));
      const next = { ...prev, blocks };
      isDirtyRef.current = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => persist(next), 800);
      return next;
    });
  }, [persist]);

  // Save on unload if dirty
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (isDirtyRef.current && proposal) persist(proposal);
    };
    // eslint-disable-next-line
  }, [proposal?.id]);

  if (error && !proposal) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] text-[#3A506B] italic">{error}</div>;
  }
  if (!proposal) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] text-[#3A506B] italic">Cargando...</div>;
  }

  const publicUrl = `${window.location.origin}/p/${proposal.share_token}`;

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copia el enlace:", publicUrl);
    }
  };

  const regenerateLink = async () => {
    if (!window.confirm("¿Regenerar el enlace público? El antiguo dejará de funcionar.")) return;
    try {
      const { share_token } = await proposalsApi.regenerateLink(proposal.id);
      setProposal({ ...proposal, share_token });
    } catch (err) {
      alert(formatApiError(err));
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="bg-[#0A192F] text-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
          <button onClick={() => navigate("/admin")} className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-[#E8C97A]" data-testid="back-dashboard">
            <ArrowLeft size={14} /> Volver
          </button>
          <div className="hidden md:block text-xs text-white/60 italic">
            {saving ? "Guardando..." : savedAt ? `Guardado · ${savedAt.toLocaleTimeString().slice(0,5)}` : "Sin cambios"}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowSaveTemplate(true)}
              data-testid="save-as-template-btn"
              title="Guardar esta propuesta como plantilla reutilizable"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs text-white/80 hover:text-[#E8C97A] border border-white/15 hover:border-[#E8C97A]/40 px-3 py-1.5 rounded-full transition-colors"
            >
              <BookmarkPlus size={12} /> Guardar plantilla
            </button>
            <select
              value={proposal.proposal_type || "informative"}
              onChange={(e) => onChange({ proposal_type: e.target.value })}
              data-testid="proposal-type-select"
              title="Cambia el tipo de propuesta"
              className="bg-[#0F2342] border border-[#E8C97A]/30 text-white text-xs px-3 py-1.5 rounded-full focus:outline-none focus:border-[#E8C97A]"
            >
              <option value="informative">Informativa · Fase de decisión</option>
              <option value="booking">Definitiva · Fase de reserva</option>
            </select>
            <select
              value={proposal.status}
              onChange={(e) => onChange({ status: e.target.value })}
              data-testid="status-select"
              className="bg-[#0F2342] border border-[#E8C97A]/30 text-white text-xs px-3 py-1.5 rounded-full focus:outline-none focus:border-[#E8C97A]"
            >
              {STATUS_OPTIONS.map(([k, l]) => (<option key={k} value={k}>{l}</option>))}
            </select>
            <button onClick={logout} className="text-white/70 hover:text-[#E8C97A]" title="Salir">
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-10 py-10">
        {error && <p className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 mb-6 text-sm">{error}</p>}

        {/* Header fields */}
        <div className="bg-white border border-[#16335A]/15 p-6 lg:p-8 mb-6" style={{ borderRadius: "2px" }}>
          <label className="block">
            <span className="block text-[0.7rem] font-medium text-[#16335A]/80 mb-1.5 uppercase tracking-wider">Cliente</span>
            <input
              value={proposal.client_name || ""}
              onChange={(e) => onChange({ client_name: e.target.value })}
              data-testid="header-client-name"
              className="w-full px-4 py-2.5 bg-white border border-[#16335A]/15 focus:border-[#16335A] focus:outline-none text-base"
              style={{ borderRadius: "2px" }}
            />
          </label>
          <label className="block mt-4">
            <span className="block text-[0.7rem] font-medium text-[#16335A]/80 mb-1.5 uppercase tracking-wider">Título del viaje</span>
            <input
              value={proposal.trip_title || ""}
              onChange={(e) => onChange({ trip_title: e.target.value })}
              placeholder="Ej: Escapada a Roma · Octubre 2026"
              data-testid="header-trip-title"
              className="w-full px-4 py-3 bg-white border border-[#16335A]/15 focus:border-[#16335A] focus:outline-none font-display text-xl"
              style={{ borderRadius: "2px" }}
            />
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <label className="block">
              <span className="block text-[0.7rem] font-medium text-[#16335A]/80 mb-1.5 uppercase tracking-wider">Destino</span>
              <input
                value={proposal.destination || ""}
                onChange={(e) => onChange({ destination: e.target.value })}
                className="w-full px-4 py-2.5 bg-white border border-[#16335A]/15 focus:border-[#16335A] focus:outline-none text-sm"
                style={{ borderRadius: "2px" }}
              />
            </label>
            <label className="block">
              <span className="block text-[0.7rem] font-medium text-[#16335A]/80 mb-1.5 uppercase tracking-wider">Desde</span>
              <input
                type="date"
                value={proposal.date_from || ""}
                onChange={(e) => onChange({ date_from: e.target.value })}
                className="w-full px-4 py-2.5 bg-white border border-[#16335A]/15 focus:border-[#16335A] focus:outline-none text-sm"
                style={{ borderRadius: "2px" }}
              />
            </label>
            <label className="block">
              <span className="block text-[0.7rem] font-medium text-[#16335A]/80 mb-1.5 uppercase tracking-wider">Hasta</span>
              <input
                type="date"
                value={proposal.date_to || ""}
                onChange={(e) => onChange({ date_to: e.target.value })}
                className="w-full px-4 py-2.5 bg-white border border-[#16335A]/15 focus:border-[#16335A] focus:outline-none text-sm"
                style={{ borderRadius: "2px" }}
              />
            </label>
          </div>
          <label className="block mt-4">
            <ImageSelector
              label="Imagen de portada"
              value={proposal.cover_image || ""}
              onChange={(v) => onChange({ cover_image: v })}
            />
          </label>
        </div>

        {/* Share box */}
        <div className="bg-[#0A192F] text-white p-6 mb-6" style={{ borderRadius: "2px" }}>
          <span className="label-eyebrow text-[#E8C97A]">Enlace público</span>
          <div className="mt-3 flex flex-col sm:flex-row gap-3 sm:items-center">
            <code className="flex-1 bg-[#0F2342] text-[#E8C97A] px-4 py-2.5 text-xs sm:text-sm overflow-x-auto" style={{ borderRadius: "2px" }}>
              {publicUrl}
            </code>
            <div className="flex gap-2">
              <button
                onClick={copyShareLink}
                data-testid="copy-share-link"
                className="bg-[#E8C97A] text-[#16335A] hover:bg-[#D4B35E] px-4 py-2.5 rounded-full text-xs font-semibold inline-flex items-center gap-2"
              >
                {copied ? <><Check size={12} /> Copiado</> : <><Copy size={12} /> Copiar</>}
              </button>
              <a
                href={publicUrl}
                target="_blank"
                rel="noreferrer"
                className="bg-white/10 hover:bg-white/15 text-white px-4 py-2.5 rounded-full text-xs font-semibold inline-flex items-center gap-2"
              >
                <ExternalLink size={12} /> Abrir
              </a>
              <a
                href={`${publicUrl}?print=full`}
                target="_blank"
                rel="noreferrer"
                title="Abrir y previsualizar para imprimir / guardar PDF"
                data-testid="admin-print-link"
                className="bg-[#E8C97A]/10 hover:bg-[#E8C97A]/20 border border-[#E8C97A]/40 text-[#E8C97A] px-4 py-2.5 rounded-full text-xs font-semibold inline-flex items-center gap-2"
              >
                <Printer size={12} /> Imprimir / PDF
              </a>
              <button
                onClick={regenerateLink}
                title="Regenerar enlace"
                className="bg-white/10 hover:bg-white/15 text-white p-2.5 rounded-full"
              >
                <RefreshCw size={12} />
              </button>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[0.65rem] font-medium text-[#E8C97A]/80 mb-1.5 uppercase tracking-wider">Contraseña (opcional)</span>
              <input
                value={proposal.share_password || ""}
                onChange={(e) => onChange({ share_password: e.target.value })}
                placeholder="Sin contraseña"
                className="w-full px-3 py-2 bg-[#0F2342] border border-white/15 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#E8C97A]"
                style={{ borderRadius: "2px" }}
              />
            </label>
            <label className="block">
              <span className="block text-[0.65rem] font-medium text-[#E8C97A]/80 mb-1.5 uppercase tracking-wider">Expira (opcional)</span>
              <input
                type="datetime-local"
                value={proposal.expires_at || ""}
                onChange={(e) => onChange({ expires_at: e.target.value })}
                className="w-full px-3 py-2 bg-[#0F2342] border border-white/15 text-white text-sm focus:outline-none focus:border-[#E8C97A]"
                style={{ borderRadius: "2px" }}
              />
            </label>
          </div>
        </div>

        {/* Block list (fixed order in Fase 1) */}
        <div className="space-y-4">
          {[...(proposal.blocks || [])].sort((a, b) => (a.order || 0) - (b.order || 0)).map((block) => (
            <BlockEditor key={block.id} block={block} onChange={(nb) => onBlockChange(block.id, nb)} proposalType={proposal.proposal_type || "informative"} />
          ))}
        </div>

        {/* Booking & Payment configuration */}
        <BookingConfigPanel
          proposal={proposal}
          onChange={onChange}
          paymentSettings={paymentSettings}
          acceptances={acceptances}
          requests={requests}
        />

        {/* Live booking summary preview (what the client will see) */}
        <div className="mt-10">
          <h3 className="font-display text-lg text-[#0A192F] mb-3">Vista previa del resumen económico</h3>
          <BookingSummary proposal={proposal} settings={paymentSettings} variant="light" />
        </div>
      </main>

      {showSaveTemplate && (
        <SaveAsTemplateModal
          proposal={proposal}
          onClose={() => setShowSaveTemplate(false)}
        />
      )}
    </div>
  );
}

function BookingConfigPanel({ proposal, onChange, paymentSettings, acceptances, requests = [] }) {
  const bookMode = proposal.book_mode || "";
  const effectiveMode = bookMode || paymentSettings?.default_book_mode || "contact_only";

  return (
    <section className="mt-10 bg-white border border-[#16335A]/15 p-6 lg:p-8" style={{ borderRadius: "2px" }} data-testid="booking-config-panel">
      <div className="flex items-center gap-3 mb-5">
        <Wallet size={18} className="text-[#16335A]" />
        <h2 className="font-display text-2xl text-[#0A192F]">Reserva y pago</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <label className="block">
          <span className="block text-[0.7rem] font-medium text-[#16335A]/80 mb-1.5 uppercase tracking-wider">Estado de pago</span>
          <select
            value={proposal.payment_status || "unpaid"}
            onChange={(e) => onChange({ payment_status: e.target.value })}
            data-testid="payment-status-select"
            className="w-full px-4 py-2.5 bg-white border border-[#16335A]/15 focus:border-[#16335A] focus:outline-none text-sm"
            style={{ borderRadius: "2px" }}
          >
            {PAYMENT_STATUS_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>

        <label className="block">
          <span className="block text-[0.7rem] font-medium text-[#16335A]/80 mb-1.5 uppercase tracking-wider">Modo del botón “Reservar ahora”</span>
          <select
            value={bookMode}
            onChange={(e) => onChange({ book_mode: e.target.value })}
            data-testid="book-mode-select"
            className="w-full px-4 py-2.5 bg-white border border-[#16335A]/15 focus:border-[#16335A] focus:outline-none text-sm"
            style={{ borderRadius: "2px" }}
          >
            {BOOK_MODE_OPTIONS.map(([k, l]) => <option key={k || "default"} value={k}>{l}</option>)}
          </select>
          <span className="block text-[0.65rem] text-[#3A506B] mt-1 italic">
            Si se deja vacío, se usa el predeterminado de “Pagos”: <strong>{paymentSettings?.default_book_mode || "contact_only"}</strong>.
          </span>
        </label>

        {effectiveMode === "custom_url" && (
          <label className="block md:col-span-2">
            <span className="block text-[0.7rem] font-medium text-[#16335A]/80 mb-1.5 uppercase tracking-wider">URL personalizada</span>
            <input
              type="url"
              value={proposal.book_url || ""}
              onChange={(e) => onChange({ book_url: e.target.value })}
              placeholder="https://..."
              data-testid="book-url-input"
              className="w-full px-4 py-2.5 bg-white border border-[#16335A]/15 focus:border-[#16335A] focus:outline-none text-sm"
              style={{ borderRadius: "2px" }}
            />
          </label>
        )}

        {effectiveMode === "contact_only" && (
          <>
            <label className="block">
              <span className="block text-[0.7rem] font-medium text-[#16335A]/80 mb-1.5 uppercase tracking-wider">Email de contacto (override)</span>
              <input
                type="email"
                value={proposal.book_contact_email || ""}
                onChange={(e) => onChange({ book_contact_email: e.target.value })}
                placeholder={paymentSettings?.contact_email || "hola@escapafy.com"}
                data-testid="book-contact-email"
                className="w-full px-4 py-2.5 bg-white border border-[#16335A]/15 focus:border-[#16335A] focus:outline-none text-sm"
                style={{ borderRadius: "2px" }}
              />
            </label>
            <label className="block">
              <span className="block text-[0.7rem] font-medium text-[#16335A]/80 mb-1.5 uppercase tracking-wider">WhatsApp (E.164, override)</span>
              <input
                type="text"
                value={proposal.book_contact_whatsapp || ""}
                onChange={(e) => onChange({ book_contact_whatsapp: e.target.value })}
                placeholder={paymentSettings?.contact_whatsapp || "+34644088078"}
                data-testid="book-contact-whatsapp"
                className="w-full px-4 py-2.5 bg-white border border-[#16335A]/15 focus:border-[#16335A] focus:outline-none text-sm"
                style={{ borderRadius: "2px" }}
              />
            </label>
          </>
        )}
      </div>

      <div className="mt-6 pt-6 border-t border-[#16335A]/10">
        <h3 className="font-display text-base text-[#0A192F] mb-4">Overrides de cálculo (opcionales)</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <label className="block">
            <span className="block text-[0.65rem] font-medium text-[#16335A]/80 mb-1.5 uppercase tracking-wider">IVA % (override)</span>
            <input
              type="number" step="0.5" min="0"
              value={proposal.iva_pct_override ?? ""}
              onChange={(e) => onChange({ iva_pct_override: e.target.value === "" ? null : Number(e.target.value) })}
              placeholder={String(paymentSettings?.iva_pct ?? 21)}
              data-testid="iva-pct-override"
              className="w-full px-3 py-2 bg-white border border-[#16335A]/15 focus:border-[#16335A] focus:outline-none text-sm"
              style={{ borderRadius: "2px" }}
            />
          </label>

          <label className="block">
            <span className="block text-[0.65rem] font-medium text-[#16335A]/80 mb-1.5 uppercase tracking-wider">Comisión gestión</span>
            <select
              value={proposal.mgmt_fee_kind_override || ""}
              onChange={(e) => onChange({ mgmt_fee_kind_override: e.target.value || null })}
              className="w-full px-3 py-2 bg-white border border-[#16335A]/15 focus:border-[#16335A] focus:outline-none text-sm"
              style={{ borderRadius: "2px" }}
            >
              <option value="">(predeterminado)</option>
              {FEE_KIND_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-[0.65rem] font-medium text-[#16335A]/80 mb-1.5 uppercase tracking-wider">Valor comisión</span>
            <input
              type="number" step="0.01" min="0"
              value={proposal.mgmt_fee_value_override ?? ""}
              onChange={(e) => onChange({ mgmt_fee_value_override: e.target.value === "" ? null : Number(e.target.value) })}
              placeholder="0"
              className="w-full px-3 py-2 bg-white border border-[#16335A]/15 focus:border-[#16335A] focus:outline-none text-sm"
              style={{ borderRadius: "2px" }}
            />
          </label>

          <label className="block">
            <span className="block text-[0.65rem] font-medium text-[#16335A]/80 mb-1.5 uppercase tracking-wider">Depósito</span>
            <select
              value={proposal.deposit_kind_override || ""}
              onChange={(e) => onChange({ deposit_kind_override: e.target.value || null })}
              data-testid="deposit-kind-override"
              className="w-full px-3 py-2 bg-white border border-[#16335A]/15 focus:border-[#16335A] focus:outline-none text-sm"
              style={{ borderRadius: "2px" }}
            >
              <option value="">(predeterminado)</option>
              {FEE_KIND_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-[0.65rem] font-medium text-[#16335A]/80 mb-1.5 uppercase tracking-wider">Valor depósito</span>
            <input
              type="number" step="0.01" min="0"
              value={proposal.deposit_value_override ?? ""}
              onChange={(e) => onChange({ deposit_value_override: e.target.value === "" ? null : Number(e.target.value) })}
              placeholder="0"
              data-testid="deposit-value-override"
              className="w-full px-3 py-2 bg-white border border-[#16335A]/15 focus:border-[#16335A] focus:outline-none text-sm"
              style={{ borderRadius: "2px" }}
            />
          </label>
        </div>
        <label className="block mt-4">
          <span className="block text-[0.65rem] font-medium text-[#16335A]/80 mb-1.5 uppercase tracking-wider">Notas internas de la reserva</span>
          <textarea
            value={proposal.booking_notes || ""}
            onChange={(e) => onChange({ booking_notes: e.target.value })}
            rows={2}
            className="w-full px-3 py-2 bg-white border border-[#16335A]/15 focus:border-[#16335A] focus:outline-none text-sm"
            style={{ borderRadius: "2px" }}
          />
        </label>
      </div>

      {acceptances && acceptances.length > 0 && (
        <div className="mt-6 pt-6 border-t border-[#16335A]/10">
          <h3 className="font-display text-base text-[#0A192F] mb-3 inline-flex items-center gap-2">
            <Users size={14} /> Aceptaciones recibidas ({acceptances.length})
          </h3>
          <div className="space-y-2">
            {acceptances.map((a) => (
              <div key={a.id} className="bg-[#FDFBF7] border border-[#16335A]/10 p-3 text-sm flex flex-wrap items-baseline gap-x-4 gap-y-1" style={{ borderRadius: "2px" }}>
                <strong className="text-[#0A192F]">{a.client_name}</strong>
                <span className="text-[#3A506B]">{a.client_email}</span>
                {a.client_phone && <span className="text-[#3A506B]">· {a.client_phone}</span>}
                <span className="text-xs text-[#3A506B]/70 ml-auto">
                  {new Date(a.accepted_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}
                </span>
                {a.notes && <p className="basis-full text-[#3A506B] italic mt-1">“{a.notes}”</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {requests && requests.length > 0 && (
        <div className="mt-6 pt-6 border-t border-[#16335A]/10" data-testid="proposal-requests-section">
          <h3 className="font-display text-base text-[#0A192F] mb-3 inline-flex items-center gap-2">
            <Users size={14} /> Solicitudes del configurador ({requests.length})
          </h3>
          <div className="space-y-3">
            {requests.map((r) => (
              <div key={r.id} className="bg-[#FDFBF7] border border-[#E8C97A]/40 p-4 text-sm" style={{ borderRadius: "2px" }} data-testid={`request-${r.id}`}>
                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                  <strong className="text-[#0A192F]">{r.client_name}</strong>
                  <span className="text-[#3A506B]">{r.client_email}</span>
                  {r.client_phone && <span className="text-[#3A506B]">· {r.client_phone}</span>}
                  <span className="text-xs text-[#3A506B]/70 ml-auto">
                    {new Date(r.created_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}
                  </span>
                </div>
                <p className="mt-2 font-display text-lg text-[#16335A]">
                  Total propuesto: <strong>{new Intl.NumberFormat("es-ES",{style:"currency",currency:"EUR"}).format(r.computed_total || 0)}</strong>
                </p>
                <div className="mt-2 text-xs text-[#3A506B] grid grid-cols-1 md:grid-cols-2 gap-1">
                  {r.snapshot?.flights && <span>✈ Vuelos: {r.snapshot.flights.name || r.snapshot.flights.airline}</span>}
                  {r.snapshot?.hotels && <span>🏨 Hotel: {r.snapshot.hotels.name}</span>}
                  {r.snapshot?.transfers && <span>🚗 Traslado: {r.snapshot.transfers.type || r.snapshot.transfers.company}</span>}
                  {r.snapshot?.restaurants?.length > 0 && <span>🍽 Restaurantes: {r.snapshot.restaurants.map(x => x.name).join(", ")}</span>}
                  {r.snapshot?.activities?.length > 0 && <span>🎟 Actividades: {r.snapshot.activities.map(x => x.name).join(", ")}</span>}
                  {r.snapshot?.excursions?.length > 0 && <span>🗺 Excursiones: {r.snapshot.excursions.map(x => x.name).join(", ")}</span>}
                </div>
                {r.notes && <p className="mt-2 text-[#3A506B] italic">“{r.notes}”</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function SaveAsTemplateModal({ proposal, onClose }) {
  const [name, setName] = useState(proposal.trip_title || proposal.destination || "");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await templatesApi.create({
        name: name.trim(),
        description: description.trim() || undefined,
        from_proposal_id: proposal.id,
      });
      setSaved(true);
      setTimeout(onClose, 1200);
    } catch (err) {
      setError(formatApiError(err));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={onClose}>
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white max-w-md w-full p-8 space-y-5"
        style={{ borderRadius: "2px" }}
        data-testid="save-template-form"
      >
        <h2 className="font-display text-2xl text-[#0A192F]">Guardar como plantilla</h2>
        <p className="text-sm text-[#3A506B]">
          Reutilizarás esta estructura (bloques, hoteles, actividades, afiliados…) sin volver a configurar todo.
          No se copian datos del cliente actual.
        </p>
        <label className="block">
          <span className="block text-xs font-medium text-[#16335A] mb-2 uppercase tracking-wider">Nombre *</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
            data-testid="template-name-input"
            placeholder="Ej: Escapada romántica a Roma · 3 noches"
            className="w-full px-4 py-3 bg-white border border-[#16335A]/20 focus:border-[#16335A] focus:outline-none text-sm"
            style={{ borderRadius: "2px" }}
          />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-[#16335A] mb-2 uppercase tracking-wider">Descripción (opcional)</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            data-testid="template-description-input"
            placeholder="Notas internas para identificar cuándo usar esta plantilla"
            className="w-full px-4 py-3 bg-white border border-[#16335A]/20 focus:border-[#16335A] focus:outline-none text-sm"
            style={{ borderRadius: "2px" }}
          />
        </label>
        {error && <p className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 text-sm">{error}</p>}
        {saved && <p className="text-green-700 bg-green-50 border border-green-200 px-4 py-3 text-sm">✓ Plantilla guardada</p>}
        <div className="flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="px-5 py-3 text-sm text-[#3A506B] hover:text-[#16335A]">Cancelar</button>
          <button
            type="submit"
            disabled={saving || saved || !name.trim()}
            data-testid="save-template-submit"
            className="bg-[#16335A] hover:bg-[#214A82] disabled:opacity-60 text-white px-6 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2"
          >
            {saving ? "Guardando..." : saved ? "Guardado" : "Guardar plantilla"}
          </button>
        </div>
      </form>
    </div>
  );
}
